/**
 * Poseidon dashboard — Backtests and forward tests (§3.2 F).
 *
 * Run parameters, data range, assumptions, version hashes and metrics; equity and
 * drawdown curves; the trade-time distribution; and the side-by-side quant-only
 * versus quant-plus-research comparison. Paper/demo forward tests are visually
 * separated from historical results, because pooling them is how a backtest gets
 * mistaken for evidence.
 */
import { useState } from 'react';
import type { BacktestMetrics, BacktestRunView, ChartPoint, ChartSeries } from '../api/types';
import { POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useServices } from '../state/Services';
import { DataTable } from '../components/DataTable';
import { BarSeriesChart, LineSeriesChart } from '../components/charts';
import { Badge, ErrorState, KeyValueList, LoadingState, Panel, Unavailable } from '../components/primitives';
import { BACKTEST_KIND_LABELS, BACKTEST_STATUS_LABELS, toneForBacktestStatus } from '../lib/labels';
import { formatDateTimeWithZone, formatDuration, formatNumber, formatPercent } from '../lib/format';

const METRIC_LABELS: readonly { key: keyof BacktestMetrics; label: string; kind: 'number' | 'percent' | 'duration' | 'money' }[] = [
  { key: 'completed_trades', label: 'Completed trades', kind: 'number' },
  { key: 'win_rate', label: 'Win rate', kind: 'percent' },
  { key: 'loss_rate', label: 'Loss rate', kind: 'percent' },
  { key: 'breakeven_rate', label: 'Breakeven rate', kind: 'percent' },
  { key: 'profit_factor', label: 'Profit factor', kind: 'number' },
  { key: 'expectancy_per_trade', label: 'Expectancy per trade', kind: 'money' },
  { key: 'average_r_multiple', label: 'Average R-multiple', kind: 'number' },
  { key: 'max_drawdown', label: 'Maximum drawdown', kind: 'money' },
  { key: 'average_holding_time_ms', label: 'Average holding time', kind: 'duration' },
  { key: 'max_holding_time_ms', label: 'Maximum holding time', kind: 'duration' },
  { key: 'exposure_utilization', label: 'Exposure utilization', kind: 'percent' },
  { key: 'margin_utilization', label: 'Margin utilization', kind: 'percent' },
  { key: 'turnover', label: 'Turnover', kind: 'money' },
  { key: 'transaction_costs', label: 'Transaction costs', kind: 'money' },
  { key: 'modeled_slippage_points', label: 'Modelled slippage (points)', kind: 'number' },
  { key: 'actual_slippage_points', label: 'Actual slippage (points)', kind: 'number' },
  { key: 'strategy_uptime_pct', label: 'Strategy uptime', kind: 'percent' },
];

function renderMetric(value: number | null, kind: 'number' | 'percent' | 'duration' | 'money'): JSX.Element {
  if (value === null) return <Unavailable reason="This metric was not produced by the run." />;
  switch (kind) {
    case 'percent':
      return <>{formatPercent(value, 2)}</>;
    case 'duration':
      return <>{formatDuration(value)}</>;
    case 'money':
      return <>{formatNumber(value, 2)}</>;
    case 'number':
      return <>{formatNumber(value, 2)}</>;
  }
}

function MetricComparison({ run }: { run: BacktestRunView }): JSX.Element {
  const modes = run.comparison;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Metric</th>
            {modes.map((mode) => (
              <th key={mode.mode} scope="col" style={{ textAlign: 'right' }}>
                {mode.mode.replace(/_/g, ' ')}
                <div className="text-faint" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                  {mode.executed_branch ? 'executed branch' : 'shadow / logged only'}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {METRIC_LABELS.map((metric) => (
            <tr key={metric.key}>
              <td>{metric.label}</td>
              {modes.map((mode) => {
                const value = mode.metrics[metric.key];
                const numeric = typeof value === 'number' ? value : null;
                return (
                  <td key={`${mode.mode}-${metric.key}`} style={{ textAlign: 'right' }} className="text-mono">
                    {renderMetric(numeric, metric.kind)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Backtests(): JSX.Element {
  const { api, isMock } = useServices();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runs = useApiData('backtests', () => api.getBacktests(), { pollMs: POLL_INTERVALS.operations, mode: api.mode });
  const runList = runs.data ?? [];
  const selected: BacktestRunView | null = runList.find((run) => run.run_id === selectedRunId) ?? runList[0] ?? null;

  const forwardRuns = runList.filter((run) => run.kind === 'paper_forward' || run.kind === 'demo_forward');
  const historicalRuns = runList.filter((run) => run.kind !== 'paper_forward' && run.kind !== 'demo_forward');

  const equitySeries: ChartSeries | null =
    selected === null
      ? null
      : {
          chart_id: 'equity_curve',
          title: `Equity and drawdown — ${selected.label}`,
          subtitle: `${selected.kind.replace(/_/g, ' ')} · ${formatDateTimeWithZone(selected.data_range.start)} → ${formatDateTimeWithZone(selected.data_range.end)}`,
          unit: 'account currency',
          series: [
            { key: 'realized_equity', label: 'Realized equity', color_token: 'series-1' },
            { key: 'marked_equity', label: 'Marked equity', color_token: 'series-2' },
            { key: 'drawdown', label: 'Drawdown', color_token: 'series-4' },
          ],
          points: selected.equity_curve.map<ChartPoint>((point) => ({
            at: point.timestamp,
            realized_equity: point.realized_equity,
            marked_equity: point.marked_equity,
            drawdown: point.drawdown,
          })),
          as_of: selected.finished_at ?? selected.created_at,
          sample_size: selected.equity_curve.length,
          notes: ['Drawdown is measured from the running high-water mark, the same definition used by the live dashboard.'],
        };

  const timeOfDaySeries: ChartSeries | null =
    selected === null
      ? null
      : {
          chart_id: 'pnl_by_hour',
          title: `Trade count and P&L by time of day — ${selected.label}`,
          subtitle: 'broker-local session hours',
          unit: 'account currency',
          series: [
            { key: 'net_pnl', label: 'Net P&L', color_token: 'series-1' },
            { key: 'count', label: 'Trades', color_token: 'series-2' },
          ],
          points: selected.trades_by_time_of_day.map<ChartPoint>((row) => ({ bucket: row.bucket, net_pnl: row.net_pnl, count: row.count })),
          as_of: selected.finished_at ?? selected.created_at,
          sample_size: selected.trades_by_time_of_day.length,
          notes: [],
        };

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Backtests &amp; forward tests</h1>
          <div className="page-header__spec">§3.2 F · parameters, hashes, §9.3 metrics, equity/drawdown curves and the quant-only vs quant-plus-research comparison</div>
        </div>
        <div className="data-clock">{isMock && <span className="mock-note">mock values</span>}</div>
      </header>

      <div className="halt-strip halt-strip--soft" style={{ alignItems: 'flex-start' }}>
        <div>
          <strong>Historical backtests and forward tests are kept separate</strong>
          <div style={{ fontSize: 12.5 }}>
            §3.2 F requires paper/demo forward-test results to be reported separately from historical backtests. {historicalRuns.length} historical
            and {forwardRuns.length} forward run(s) are loaded. A result that only survives the optimistic cost model, or only one market
            regime, is not evidence (§9.3).
          </div>
        </div>
      </div>

      <Panel title="Runs" subtitle="§3.2 F · parameters, data range, assumptions and version hashes" mock={isMock} flush>
        {runs.error !== null && runs.data === null ? (
          <ErrorState error={runs.error} context="Backtest runs could not be loaded." onRetry={runs.refresh} />
        ) : runs.data === null ? (
          <LoadingState label="Loading backtest runs…" />
        ) : (
          <DataTable
            rows={runList}
            rowKey={(row) => row.run_id}
            onRowClick={(row) => setSelectedRunId(row.run_id)}
            initialSort={{ key: 'created', direction: 'desc' }}
            rowTone={(row) => (row.status === 'failed' ? 'danger' : row.status === 'running' ? 'warn' : null)}
            columns={[
              { key: 'label', header: 'Run', sortValue: (row) => row.label, render: (row) => <><strong>{row.label}</strong><div className="text-faint text-mono" style={{ fontSize: 11 }}>{row.run_id}</div></> },
              { key: 'kind', header: 'Kind', sortValue: (row) => row.kind, render: (row) => BACKTEST_KIND_LABELS[row.kind] },
              { key: 'status', header: 'Status', sortValue: (row) => row.status, render: (row) => <Badge tone={toneForBacktestStatus(row.status)}>{BACKTEST_STATUS_LABELS[row.status]}</Badge> },
              { key: 'created', header: 'Created / finished', sortValue: (row) => row.created_at, render: (row) => <span className="text-mono" style={{ fontSize: 11.5 }}>{formatDateTimeWithZone(row.created_at)}<div className="text-faint">{row.finished_at === null ? 'still running' : formatDateTimeWithZone(row.finished_at)}</div></span> },
              { key: 'trades', header: 'Trades', align: 'right', sortValue: (row) => row.metrics.completed_trades, render: (row) => row.metrics.completed_trades },
              { key: 'pf', header: 'PF / expectancy', align: 'right', sortValue: (row) => row.metrics.profit_factor, render: (row) => <span className="text-mono">{row.metrics.profit_factor === null ? '—' : `${formatNumber(row.metrics.profit_factor, 2)}×`} / {row.metrics.expectancy_per_trade === null ? '—' : formatNumber(row.metrics.expectancy_per_trade, 2)}</span> },
              { key: 'artifact', header: 'Reproducibility', render: (row) => (row.repro_artifact === null ? <Unavailable reason="No artifact recorded, so this run cannot be reproduced." /> : <span className="text-mono" style={{ fontSize: 11 }}>{row.repro_artifact.path}<div className="text-faint">{row.repro_artifact.hash}</div></span>) },
            ]}
            emptyTitle="No backtest runs"
          />
        )}
      </Panel>

      {selected !== null && (
        <>
          <Panel
            title={`Run detail — ${selected.label}`}
            subtitle={`${BACKTEST_KIND_LABELS[selected.kind]} · ${BACKTEST_STATUS_LABELS[selected.status]}`}
            mock={isMock}
            actions={
              selected.repro_artifact !== null ? (
                <span className="text-faint text-mono" style={{ fontSize: 11 }}>
                  {selected.repro_artifact.hash}
                </span>
              ) : (
                <Badge tone="warn">no artifact</Badge>
              )
            }
            footnote={selected.notes ?? undefined}
          >
            <div className="grid grid--2">
              <div>
                <div className="ribbon__label">Parameters</div>
                <KeyValueList items={Object.entries(selected.parameters).map(([key, value]) => ({ key, value: String(value) }))} />
                <div className="ribbon__label" style={{ marginTop: 12 }}>
                  Data range and provenance
                </div>
                <KeyValueList
                  items={[
                    { key: 'Start', value: formatDateTimeWithZone(selected.data_range.start) },
                    { key: 'End', value: formatDateTimeWithZone(selected.data_range.end) },
                    { key: 'Timezone', value: selected.data_range.timezone },
                    { key: 'Provenance', value: selected.data_range.provenance },
                  ]}
                />
              </div>
              <div>
                <div className="ribbon__label">Assumptions (§9.1)</div>
                <KeyValueList
                  items={[
                    { key: 'Spread model', value: selected.assumptions.spread_model },
                    { key: 'Commission', value: selected.assumptions.commission_model },
                    { key: 'Swap / financing', value: selected.assumptions.swap_model },
                    { key: 'Slippage', value: selected.assumptions.slippage_model },
                    { key: 'Fill model', value: selected.assumptions.fill_model },
                    { key: 'Intrabar', value: selected.assumptions.intrabar_model ?? 'not modelled — treats intrabar ordering as ambiguous' },
                  ]}
                />
                <div className="ribbon__label" style={{ marginTop: 12 }}>
                  Frozen versions (§9.1, §18.6)
                </div>
                <KeyValueList
                  items={[
                    { key: 'Strategy', value: <span className="text-mono">{selected.versions.strategy_version}</span> },
                    { key: 'Features', value: <span className="text-mono">{selected.versions.feature_version}</span> },
                    { key: 'Regime classifier', value: <span className="text-mono">{selected.versions.regime_version}</span> },
                    { key: 'Eligibility config', value: <span className="text-mono">{selected.versions.eligibility_config_hash}</span> },
                    { key: 'Model', value: <span className="text-mono">{selected.versions.model_version ?? 'n/a'}</span> },
                    { key: 'Cost model', value: <span className="text-mono">{selected.versions.cost_model_version}</span> },
                    { key: 'Code commit', value: <span className="text-mono">{selected.versions.code_commit}</span> },
                    { key: 'Config hash', value: <span className="text-mono">{selected.versions.config_hash}</span> },
                  ]}
                />
              </div>
            </div>
          </Panel>

          {equitySeries !== null && equitySeries.points.length > 0 && (
            <Panel title="Equity and drawdown curve" subtitle="realized equity, marked equity and drawdown together" mock={isMock} flush>
              <div className="panel__body">
                <LineSeriesChart series={equitySeries} height={300} />
              </div>
            </Panel>
          )}

          <div className="grid grid--2">
            {timeOfDaySeries !== null && timeOfDaySeries.points.length > 0 && (
              <Panel title="Trade count and P&L by time of day" subtitle="§9.3" mock={isMock}>
                <BarSeriesChart series={timeOfDaySeries} height={240} />
              </Panel>
            )}

            <Panel title="Daily drawdown distribution" subtitle="§9.3" mock={isMock}>
              <BarSeriesChart
                height={240}
                series={{
                  chart_id: 'equity_curve',
                  title: 'Daily drawdown distribution',
                  subtitle: null,
                  unit: 'days',
                  series: [{ key: 'count', label: 'Days', color_token: 'series-1' }],
                  points: selected.daily_drawdown_distribution.map((row) => ({ bucket: row.bucket, count: row.count })),
                  as_of: selected.finished_at ?? selected.created_at,
                  sample_size: selected.daily_drawdown_distribution.length,
                  notes: [],
                }}
              />
            </Panel>
          </div>

          <Panel title="Quant-only vs quant-plus-research" subtitle="§9.3 · §16.3.2 shadow A/B mode" mock={isMock} flush>
            <MetricComparison run={selected} />
          </Panel>

          <div className="grid grid--2">
            <Panel title="P&L by symbol" subtitle="§9.3" mock={isMock} flush>
              <DataTable
                rows={selected.pnl_by_symbol}
                rowKey={(row) => row.key}
                initialSort={{ key: 'pnl', direction: 'desc' }}
                columns={[
                  { key: 'key', header: 'Symbol', sortValue: (row) => row.key, render: (row) => row.key },
                  { key: 'trades', header: 'Trades', align: 'right', sortValue: (row) => row.trades, render: (row) => row.trades },
                  { key: 'pnl', header: 'Net P&L', align: 'right', sortValue: (row) => row.net_pnl, render: (row) => <span className={row.net_pnl >= 0 ? 'text-ok' : 'text-danger'}>{formatNumber(row.net_pnl, 2)}</span> },
                ]}
                emptyTitle="No per-symbol breakdown"
              />
            </Panel>

            <Panel title="P&L by regime" subtitle="§9.3 · regime classifier must be frozen per run (§18.6)" mock={isMock} flush>
              <DataTable
                rows={selected.pnl_by_regime}
                rowKey={(row) => row.key}
                initialSort={{ key: 'pnl', direction: 'desc' }}
                columns={[
                  { key: 'key', header: 'Regime', sortValue: (row) => row.key, render: (row) => row.key },
                  { key: 'trades', header: 'Trades', align: 'right', sortValue: (row) => row.trades, render: (row) => row.trades },
                  { key: 'pnl', header: 'Net P&L', align: 'right', sortValue: (row) => row.net_pnl, render: (row) => <span className={row.net_pnl >= 0 ? 'text-ok' : 'text-danger'}>{formatNumber(row.net_pnl, 2)}</span> },
                ]}
                emptyTitle="No per-regime breakdown"
              />
            </Panel>
          </div>
        </>
      )}

      <Panel title="Reading these results honestly" subtitle="§9.1, §9.3, §17.2">
        <ul className="consequences" style={{ paddingLeft: 18 }}>
          <li>Fill assumptions matter more than the headline P&L: a mid-price fill or a decision at the close of the same bar inflates results.</li>
          <li>Data provenance is unresolved in the spec (§17.2). Until the bar/ticks source is pinned down, treat these numbers as illustrative of the pipeline, not of an edge.</li>
          <li>Costs must be stressed: a result that disappears when spread and slippage are increased is not a result.</li>
          <li>Results must hold across more than one market regime; a single-period spike is not evidence of robustness.</li>
          <li>Forward demo results are the ones that matter, and they need months, not days (§13 Phase 5).</li>
        </ul>
      </Panel>
    </>
  );
}
